import { AdminContainer, AlignedListContainer } from "../../components/layouts";
import {
  BaseTextListItem,
  GutterlessListItem as ListItem
} from "../../components/lists";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import { merge, remove, startCase, camelCase } from "lodash";
import humps from "humps";
import { propertiesManagementSystems, partnerErrorMessages } from "./constants";
import { hasPms, hasAnyPms } from "../../utils/partnerPms";
import { Link as RouterLink } from "react-router-dom";
import Features from "./slices/Features/Features";

import AddIcon from "@material-ui/icons/Add";
import AdminActionLinks from "../../components/AdminActionLinks";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import CopyContentIconButton from "../../components/CopyContentIconButton";
import DetailSection from "../../components/DetailSection";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ExternalLinksList from "../../components/ExternalLinksList";
import InlineEditable from "../../components/InlineEditable";
import Link from "../../components/Link";
import ListItemText from "@material-ui/core/ListItemText";
import Money from "../../lithic/Money";
import React, { Suspense, useEffect } from "react";
import SafeExternalLink from "../../lithic/SafeExternalLink";
import SimpleTable from "../../components/SimpleTable";
import Switch from "@material-ui/core/Switch";
import Time from "../../lithic/Moment";
import TransferList from "./components/TransferList";
import api from "../../api";
import config from "../../config";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core";
import { useError } from "../../components/FormError";
import { useParams } from "react-router";
import useToggle from "../../state/useToggle";
import AssetUpload, { createAssetFromFile } from "../../components/AssetUpload";
import { PartnerColorsSlice } from "./components/PartnerColorsSlice";
import dayjs from "dayjs";
import DatePicker from "../../components/DatePicker";
import AirlineStopsIcon from "@mui/icons-material/AirlineStops";
import { BorderRadiusSlice } from "./components/BorderRadiusSlice";
import { FontsSlice } from "./components/FontsSlice";
import { useUser } from "../../hooks/user";
import { toast } from "react-toastify";
import { CreditModelPriceInput } from "./components/CreditModelPriceInput";
import PostYardiCharges from "./components/PostYardiCharges";
import LedgerConfigurationModal from "./components/LedgerConfigurationModal";
import TransUnionCredsModal from "./components/TransUnionCredsModal";
import FillCustomerIdentityViaCsv from "./components/FillCustomerIdentityViaCsv";
import { CrmBillingSlice } from "./components/CrmBillingSlice";
import { PhoneNumberBillingSlice } from "./components/PhoneNumberBillingSlice";
import { BillingSlice } from "./components/BillingSlice";
import { PhoneNumbersSlice } from "./components/PhoneNumbersSlice";
import { PartnerBackgroundColors } from "./slices/PartnerBackgroundSlice";
import { getFileUploadErrorMessage } from "../../utils/fileUploadError";

const AuditLogs = React.lazy(() => import("../../components/AuditLogs"));

const useStyles = makeStyles((theme) => ({
  active: {
    color: theme.palette.success.main,
    fontWeight: "bold",
    textTransform: "uppercase"
  },
  iconButton: {
    paddingTop: 0,
    paddingBottom: 0
  },
  started: {
    color: theme.palette.secondary.main,
    fontWeight: "bold",
    textTransform: "uppercase"
  },
  submitted: {
    color: theme.palette.warning.main,
    fontWeight: "bold",
    textTransform: "uppercase"
  },
  nonClickableLabel: {
    display: "flex",
    gap: 10,
    alignItems: "center"
  },
  logoWrapper: {
    padding: "10px",
    boxShadow: "0px 10px 15px -3px rgba(0,0,0,0.1)"
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  pageLayout: {
    display: "flex",
    gap: theme.spacing(3)
  },
  pageContent: {
    flex: 1,
    minWidth: 0
  },
  gistSidebar: {
    width: 200,
    flexShrink: 0,
    position: "sticky",
    top: theme.spacing(2),
    alignSelf: "flex-start",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    padding: theme.spacing(1.5),
    borderRight: `1px solid ${theme.palette.divider}`,
    "&::-webkit-scrollbar": {
      width: 4
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: 2
    }
  },
  gistTitle: {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    letterSpacing: "0.05em"
  },
  gistLink: {
    display: "block",
    padding: `${theme.spacing(0.5)}px 0`,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.main
    }
  }
}));

const SERVICE_TYPES = {
  landlord: "Landlord",
  full_service: "Full Service"
};

function TextListItem(props) {
  return <BaseTextListItem disableGutters {...props} />;
}

const verificationNames = [
  "rental_application",
  "identity_verification",
  "verified_income_verification",
  "combined_income_verification",
  "combined_income_re_verification",
  "credit_report",
  "criminal_report",
  "canadian_criminal_report",
  "housing_history",
  "rental_verification",
  "eviction_report",
  "premium_rental_history",
  "identity_verification_tin"
];

const CREDITSCORE_MODELS = {
  "05402": "VantageScore 4.0",
  "05206": "FICO 9"
};

const supportedPropertyManagementSystemForPostingCharges = ["rent_manager", "entrata", "appfolio", "revela"];

const CompactListItem = (props) => (
  <ListItem {...props} style={{ width: "fit-content", ...(props.style || {}) }} />
);

const DEPRECATED_CHIP_STYLE = { fontSize: "0.65rem", height: 18, color: "#f57c00", borderColor: "#f57c00" };
const userLinkStyle = { color: "#e6007e" };

const GIST_SECTIONS = [
  { id: "partner-info", label: "Partner Info" },
  { id: "logo", label: "Logo" },
  { id: "users", label: "Users" },
  { id: "pms-integrations", label: "Property Management System Integrations" },
  { id: "customization", label: "Customization" },
  { id: "contact", label: "Contact & Address" },
  { id: "integration-links", label: "Integration Links" },
  { id: "financial-config", label: "Financial Config" },
  { id: "criminal-pricing", label: "Criminal Report Pricing" },
  { id: "partner-events", label: "Partner Events" },
  { id: "pricing-tiers", label: "Pricing Tiers" },
  { id: "exclusion-list", label: "Exclusion List" },
  { id: "monthly-billing", label: "Monthly Billing" },
  { id: "credit-bureau-ids", label: "Credit Bureau IDs" },
  { id: "billing", label: "Stripe Billing" },
  { id: "features", label: "Features" },
  { id: "boomscreen-email", label: "BoomScreen Email" },
  { id: "crm-billing", label: "CRM Billing" },
  { id: "rent-reporting", label: "Rent Reporting" },
  { id: "credit-monitoring", label: "Credit Monitoring" },
  { id: "whitelist", label: "Whitelist" },
  { id: "external-links", label: "External Links" },
  { id: "ledger-config", label: "Ledger Config" },
  { id: "admin-actions", label: "Admin Actions" },
  { id: "payment-instruments", label: "Payment Instruments" },
  { id: "money-tools", label: "Money Tools" },
  { id: "audit-logs", label: "Audit Logs" },
  { id: "pets", label: "Pets" },
  { id: "verification-prices", label: "Verification Prices" }
];

function GistSidebar({ classes }) {
  return (
    <nav className={classes.gistSidebar}>
      <div className={classes.gistTitle}>On this page</div>
      {GIST_SECTIONS.map((section) => (
        <a
          key={section.id}
          href={`#${section.id}`}
          className={classes.gistLink}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          {section.label}
        </a>
      ))}
    </nav>
  );
}

export default function PartnerDetailPage({ partnerId }) {
  const { id } = useParams();
  const classes = useStyles();
  const [partner, setPartner] = React.useState({});
  const pricingTierToggle = useToggle(false);
  const [pricingTierError, setPricingTierError] = useError();
  const [selectedPricingTier, setSelectedPricingTier] = React.useState({});
  const [tiers, setTiers] = React.useState([{}]);

  const [upcomingStatements, setUpcomingStatements] = React.useState([]);
  const [upcomingDialogOpen, setUpcomingDialogOpen] = React.useState(false);
  const [paymentInstruments, setPaymentInstruments] = React.useState([]);
  const [autoEnrollmentEnabled, setAutoEnrollmentEnabled] =
    React.useState(false);
  const [autoEnrollmentDefault, setAutoEnrollmentDefault] = React.useState("");

  useEffect(() => {
    const identifier = partnerId || id;
    if (identifier)
      Promise.all([
        api
          .getPartner({ id: partnerId || id })
          .then(api.pickData)
          .then((partnerData) => {
            setPartner(partnerData);
            setAutoEnrollmentEnabled(
              Boolean(partnerData.autoEnrollmentEnabledDate)
            );
            setAutoEnrollmentDefault(partnerData.autoEnrollmentDefault || "");
          }),
        api
          .getPartnerUpcomingStatements({ id: partnerId || id })
          .then(api.pickData)
          .then(setUpcomingStatements),
        api
          .getPartnerPaymentInstruments({ id: partnerId || id })
          .then(api.pick("data.items"))
          .then(setPaymentInstruments)
      ]);
  }, [id, partnerId]);

  function saveAttribute(params) {
    return api
      .updatePartner({
        id,
        ...params,
        contactFirstName: partner.contactFirstName || "",
        contactLastName: partner.contactLastName || ""
      })
      .then(api.pickData)
      .then(setPartner)
      .catch((error) => {
        const rawMessage = error?.response?.data?.error?.message;
        const message =
          partnerErrorMessages[rawMessage] ||
          rawMessage ||
          "Failed to update partner";
        toast.error(message);
        throw error;
      });
  }

  const isPayout = upcomingStatements.balance?.cents > 0;
  const UpcomingTitle = isPayout ? "Upcoming payout" : "Upcoming charge";

  const { user } = useUser();
  const isSuperAdmin = user.roles.includes("super_admin");

  const handleActionSuccess = async (resp) => {
    if (resp?.config?.url?.includes("/post_rent_reporting_charges"))
      toast.success("Job is in progress, check the PMS queue for the events");

    if (resp?.config?.url?.includes("/create_plaid_customer")) {
      const isPendingEnablement =
        resp.data?.endCustomer?.status === "PENDING_ENABLEMENT";
      const isProduction = config.appEnv === "production";

      setPartner((prev) => ({
        ...prev,
        plaidClientId: resp.data?.endCustomer?.clientId,
        pendingPlaidEnablement: isPendingEnablement && !isProduction
      }));

      if (isPendingEnablement && isProduction)
        try {
          await api.post(
            `/admin/v1/partners/${partner.id}/enable_plaid_customer`,
            {}
          );
          toast.success("Plaid for BoomScreen enabled successfully");
        } catch (enableError) {
          const plaidError = enableError?.response?.data?.error?.message;
          toast.error(
            `Error enabling Plaid: ${
              plaidError?.errorMessage ||
              plaidError?.error ||
              "Failed to enable Plaid for BoomScreen"
            }`
          );
        }
      else toast.success("Plaid customer created successfully");

      return;
    }
    setPartner(resp.data);
  };

  const handleActionError = (error) => {
    const url = error?.config?.url || "";
    if (
      url.includes("/create_plaid_customer") ||
      url.includes("/enable_plaid_customer")
    ) {
      const plaidError = error?.response?.data?.error?.message;
      if (plaidError?.errorCode === "CHANNEL_CONFLICT")
        toast.error(
          "Channel conflict. Please contact Plaid to resolve and try again"
        );
      else {
        const errorMessage =
          plaidError?.errorMessage ||
          plaidError?.error ||
          "Failed to enable Plaid";
        toast.error(`Error enabling Plaid: ${errorMessage}`);
      }
      return;
    }
    toast.error(
      error?.response?.data?.message || error?.message || "An error occurred"
    );
  };

  const [newPassword, setNewPassword] = React.useState("");
  const [partnerUserModal, setPartnerUserModal] = React.useState(false);
  const [creditReportPricePerModel, setCreditReportPricePerModel] =
    React.useState([]);
  const [oneOffChargeModal, setOneOffChargeModal] = React.useState(false);
  const [oneOffChargeData, setOneOffChargeData] = React.useState({
    amount: "",
    description: "",
    category: "manual_one_off_charge",
    countTowardMonthlyMinimum: true,
    operationType: "charge"
  });

  const [recurringCharges, setRecurringCharges] = React.useState([]);
  const [recurringChargesDialogOpen, setRecurringChargesDialogOpen] = React.useState(false);
  const [recurringChargeFormOpen, setRecurringChargeFormOpen] = React.useState(false);
  const [recurringChargeData, setRecurringChargeData] = React.useState({ amount: "", description: "" });
  const [editingRecurringChargeId, setEditingRecurringChargeId] = React.useState(null);
  const [recurringChargeSubmitting, setRecurringChargeSubmitting] = React.useState(false);

  const fetchRecurringCharges = React.useCallback(() => {
    if (!partner.id) return;
    api.getPartnerRecurringCharges({ id: partner.id })
      .then(api.pickData)
      .then(setRecurringCharges)
      .catch(() => {});
  }, [partner.id]);

  React.useEffect(() => {
    fetchRecurringCharges();
  }, [fetchRecurringCharges]);

  React.useEffect(() => {
    setTiers([...(partner.pricingTiers || []), {}]);
  }, [partner]);

  const [defaultScreeningFees, setDefaultScreeningFees] = React.useState({});
  React.useEffect(() => {
    api
      .getVerificationSchemas()
      .then(api.pickData)
      .then((schemas) => {
        const fees = schemas.reduce((acc, schema) => {
          if (schema.name === "credit_report") {
            const obj = schema.pricing.reduce((acc, pricing) => {
              return {
                ...acc,
                [pricing.name]: pricing.amount.cents
              };
            }, {});

            setCreditReportPricePerModel(obj);
          }

          return {
            ...acc,
            [schema.name]: schema.screeningFee?.cents || 0
          };
        }, {});
        setDefaultScreeningFees(fees);
      });
  }, []);

  const verificationSchemaPrice = (schemaName) => {
    // combined_income_re_verification isn't a real schema but a flow inside
    // combined_income_verification, should default to its price unless overridden
    const customDefaults = {
      combined_income_re_verification:
        defaultScreeningFees["combined_income_verification"] || 0
    };

    return (
      partner.boomScreenScreeningFees?.[humps.camelize(schemaName)] ??
      defaultScreeningFees[schemaName] ??
      customDefaults[schemaName] ??
      0
    );
  };
  const getCreditModelPrice = (model) => {
    return (
      partner.boomScreenScreeningFees?.[humps.camelize(model)] ??
      creditReportPricePerModel[model] ??
      0
    );
  };

  const setLogo = (files) => {
    const owner = {
      owner_id: partner.id,
      owner_type: "Boom::Partner"
    };

    const subject = {
      subject_id: partner.id,
      subject_type: "Boom::Partner"
    };

    createAssetFromFile(files, owner, subject)
      .then(({ data }) =>
        api.setPartnerLogo(partner.id, { asset_id: data.asset.id })
      )
      .then(api.pickData)
      .then((partner) => {
        setPartner({ ...partner });
      })
      .catch((err) => toast.error(getFileUploadErrorMessage(err) || err.message));
  };

  return (
    <AdminContainer variant="detail-wide" customer={partner.customer}>
      <div className={classes.pageLayout}>
      <GistSidebar classes={classes} />
      <div className={classes.pageContent}>
      <AlignedListContainer maxWidth="xl">
        <div id="partner-info">
        <ListItem>
          <ListItemText>Partner Id: {partner.id}</ListItemText>
        </ListItem>

        <InlineEditable
          label="Partner Name"
          value={partner.name}
          displayValue={partner.name}
          ListItemComponent={ListItem}
          onCommit={(name) => saveAttribute({ name })}
        />

        <TextListItem>
          Created:{" "}
          <Time format="MMM DD, YYYY HH:mm:ss">{partner.createdAt}</Time>
        </TextListItem>
        <TextListItem>
          Status:{" "}
          <span
            className={clsx({
              [classes.submitted]: partner.status === "submitted",
              [classes.active]: partner.status === "active",
              [classes.started]: partner.status === "started"
            })}
          >
            {partner.status}
          </span>
        </TextListItem>

        <TextListItem>
          Notify partner about started application:{" "}
          <Switch
            checked={Boolean(partner.notifyAboutStartedApplication)}
            color="primary"
            onChange={(ev) =>
              saveAttribute({
                settings: {
                  ...partner.settings,
                  notification_settings: {
                    application_started: ev.target.checked
                  }
                }
              })
            }
          />
        </TextListItem>

        </div>
        <DetailSection title="Logo" id="logo">
          <div className={classes.logoWrapper}>
            <div>
              {partner?.logoUrl ? (
                <img style={{ width: "120px" }} src={partner.logoUrl} alt="" />
              ) : (
                "No logo"
              )}
            </div>
            <div>
              <AssetUpload
                dropzoneLabel="Drag or Select a logo"
                onAssetDropped={setLogo}
                disableDrop={!partner?.id}
              />
            </div>
          </div>
        </DetailSection>

        <Paper
          id="users"
          elevation={3}
          style={{
            padding: 16,
            marginTop: 16,
            marginBottom: 16,
            border: "2px solid #1976d2",
            backgroundColor: "#f5f9ff"
          }}
        >
          <DetailSection title={`Users`}>
            <SimpleTable
              truncated
              tableProps={{ size: "small" }}
              headers={[
                "ID",
                "Name",
                "Email",
                "Created At",
                "Impersonate",
                "Role",
                ""
              ]}
              rows={[...(partner.users || [])].sort((a, b) => {
                const aBoompay = a.email?.endsWith("@boompay.app") ? 0 : 1;
                const bBoompay = b.email?.endsWith("@boompay.app") ? 0 : 1;
                return aBoompay - bBoompay;
              })}
              keyRowAttr="id"
              toCells={(row) => [
                row.id,
                row.name,
                <RouterLink key={`user-link-${row.id}`} to={`/partners/${partner.id}/users/${row.id}`} style={userLinkStyle}>{row.email}</RouterLink>,
                row.createdAt,
                <ImpersonateUserAction
                  key={`pt`}
                  onClick={(user) => {
                    api
                      .impersonatePartnerUser(partner.id, user)
                      .then((response) => {
                        const url =
                          response.data?.loginUrl || response.data?.login_url;
                        if (url) {
                          window.open(url, "_blank");
                        }
                      });
                  }}
                  user={row}
                  disabled={partner.features?.includes("disallow_impersonate")}
                  partnerName={partner.name}
                />,
                row.role,
                <Button
                  variant="contained"
                  color="primary"
                  key={`action-${row.id}`}
                  disabled={!isSuperAdmin}
                  onClick={() => {
                    setNewPassword("");
                    setPartnerUserModal({ id: row.id, role: row.role });
                  }}
                >
                  Edit
                </Button>
              ]}
            />
          </DetailSection>
        </Paper>

        <div id="customization">
        <FontsSlice
          partner={partner}
          onSave={({ fontFamily, fontFamilyLink }) => {
            saveAttribute({
              settings: {
                ...partner.settings,
                fontFamily: fontFamily,
                fontFamilyLink: fontFamilyLink
              }
            });
          }}
        />

        <BorderRadiusSlice
          partner={partner}
          onSave={(borderRadius) => {
            saveAttribute({
              settings: {
                base_border_radius: borderRadius
              }
            });
          }}
        />

        <PartnerColorsSlice
          partner={partner}
          onSave={(primary) => {
            saveAttribute({
              settings: {
                colors: {
                  primary: primary
                }
              }
            });
          }}
        />

        <PartnerBackgroundColors
          partner={partner}
          onSave={(background) => {
            saveAttribute({
              settings: {
                colors: {
                  background: background
                }
              }
            });
          }}
        />

        </div>
        <div id="contact">
        <ListItem>
          <ListItemText>Total Customers: {partner.totalCustomers}</ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Contact First Name: {partner.contactFirstName}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Contact Last Name: {partner.contactLastName}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Contact Phone: {partner.contactPhoneNumber}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>Contact Email: {partner.email}</ListItemText>
        </ListItem>

        <InlineEditable
          label="Street Address"
          value={partner.legalEntity?.address?.address1 || ""}
          displayValue={partner.legalEntity?.address?.address1 || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(street) =>
            saveAttribute({
              address: {
                street,
                city: partner.legalEntity?.address?.city || "",
                region: partner.legalEntity?.address?.stateOrProvince || "",
                postal_code: partner.legalEntity?.address?.postalCode || "",
                country_code: partner.legalEntity?.address?.country || "US"
              }
            })
          }
        />

        <InlineEditable
          label="City"
          value={partner.legalEntity?.address?.city || ""}
          displayValue={partner.legalEntity?.address?.city || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(city) =>
            saveAttribute({
              address: {
                street: partner.legalEntity?.address?.address1 || "",
                city,
                region: partner.legalEntity?.address?.stateOrProvince || "",
                postal_code: partner.legalEntity?.address?.postalCode || "",
                country_code: partner.legalEntity?.address?.country || "US"
              }
            })
          }
        />

        <InlineEditable
          label="State/Region"
          value={partner.legalEntity?.address?.stateOrProvince || ""}
          displayValue={
            partner.legalEntity?.address?.stateOrProvince || "(not set)"
          }
          ListItemComponent={ListItem}
          onCommit={(region) =>
            saveAttribute({
              address: {
                street: partner.legalEntity?.address?.address1 || "",
                city: partner.legalEntity?.address?.city || "",
                region,
                postal_code: partner.legalEntity?.address?.postalCode || "",
                country_code: partner.legalEntity?.address?.country || "US"
              }
            })
          }
        />

        <InlineEditable
          label="Postal Code"
          value={partner.legalEntity?.address?.postalCode || ""}
          displayValue={partner.legalEntity?.address?.postalCode || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(postal_code) =>
            saveAttribute({
              address: {
                street: partner.legalEntity?.address?.address1 || "",
                city: partner.legalEntity?.address?.city || "",
                region: partner.legalEntity?.address?.stateOrProvince || "",
                postal_code,
                country_code: partner.legalEntity?.address?.country || "US"
              }
            })
          }
        />

        <InlineEditable
          label="Country Code"
          value={partner.legalEntity?.address?.country || ""}
          displayValue={partner.legalEntity?.address?.country || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(country_code) =>
            saveAttribute({
              address: {
                street: partner.legalEntity?.address?.address1 || "",
                city: partner.legalEntity?.address?.city || "",
                region: partner.legalEntity?.address?.stateOrProvince || "",
                postal_code: partner.legalEntity?.address?.postalCode || "",
                country_code: country_code.toUpperCase()
              }
            })
          }
        />

        <InlineEditable
          label="Property Type"
          value={partner.propertyType || ""}
          displayValue={
            {
              single_family: "Single family",
              multi_family: "Multi family",
              manufactured_housing: "Manufactured housing"
            }[partner.propertyType] || "(not set)"
          }
          ListItemComponent={ListItem}
          inputType="select"
          inputProps={{
            options: [
              { label: "(not set)", value: "" },
              { label: "Single family", value: "single_family" },
              { label: "Multi family", value: "multi_family" },
              { label: "Manufactured housing", value: "manufactured_housing" }
            ]
          }}
          onCommit={(propertyType) =>
            saveAttribute({ propertyType: propertyType || null })
          }
        />

        </div>
        <div id="integration-links">
        <TextListItem>
          Plaid Web Auth Link:{" "}
          <SafeExternalLink href={partner.plaidAuthLink}>
            {partner.plaidAuthLink}
          </SafeExternalLink>
          <CopyContentIconButton
            value={partner.plaidAuthLink}
            className={classes.iconButton}
          />
        </TextListItem>
        <TextListItem>
          Direct ACH Link (unsafe):{" "}
          <SafeExternalLink href={partner.achLinkerLink}>
            {partner.achLinkerLink}
          </SafeExternalLink>
          <CopyContentIconButton
            value={partner.achLinkerLink}
            className={classes.iconButton}
          />
        </TextListItem>
        <InlineEditable
          label="Webhook URL"
          value={partner.webhookUrl}
          ListItemComponent={ListItem}
          displayValue={partner.webhookUrl}
          onCommit={(webhookUrl) => saveAttribute({ webhookUrl })}
        />
        </div>
        <div id="financial-config">
        <InlineEditable
          label="Invoice Bank Account"
          value={partner.invoiceBankAccount?.id || ""}
          displayValue={partner.invoiceBankAccount?.label || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(invoiceBankAccountId) =>
            saveAttribute({ invoiceBankAccountId })
          }
          inputType="select"
          inputProps={{
            options: (partner.bankAccounts || []).map((ba) => ({
              value: ba.id,
              label: ba.label
            }))
          }}
        />
        <InlineEditable
          label="Payout Bank Account"
          value={partner.payoutBankAccount?.id || ""}
          displayValue={partner.payoutBankAccount?.label || "(not set)"}
          ListItemComponent={ListItem}
          onCommit={(payoutBankAccountId) =>
            saveAttribute({ payoutBankAccountId })
          }
          inputType="select"
          inputProps={{
            options: (partner.bankAccounts || []).map((ba) => ({
              value: ba.id,
              label: ba.label
            }))
          }}
        />
        <InlineEditable
          label="Company URL"
          value={partner.url}
          ListItemComponent={ListItem}
          displayValue={
            <SafeExternalLink href={partner.url}>
              {partner.url}
            </SafeExternalLink>
          }
          onCommit={(url) => saveAttribute({ url })}
        />
        <InlineEditable
          label="Service Type"
          value={partner.serviceType}
          displayValue={partner.serviceType}
          ListItemComponent={ListItem}
          onCommit={(serviceType) => saveAttribute({ serviceType })}
          inputType="select"
          inputProps={{
            options: ["landlord", "full_service"].map((st) => ({
              value: st,
              label: SERVICE_TYPES[st]
            }))
          }}
        />
        <InlineEditable
          label={
            <span>
              Property Management System{" "}
              <Chip
                label="Deprecated"
                size="small"
                variant="outlined"
                style={DEPRECATED_CHIP_STYLE}
              />
            </span>
          }
          value={partner.propertyManagementSystem}
          displayValue={startCase(partner.propertyManagementSystem)}
          ListItemComponent={ListItem}
          onCommit={(propertyManagementSystem) =>
            saveAttribute({ propertyManagementSystem })
          }
          inputType="select"
          inputProps={{
            options: ["", ...propertiesManagementSystems].map((value) => ({
              value,
              label: value ? startCase(value) : "-"
            }))
          }}
        />
        <InlineEditable
          label="Revenue Collection Channel"
          value={partner.revenueCollectionChannel || ""}
          displayValue={startCase(partner.revenueCollectionChannel)}
          ListItemComponent={ListItem}
          onCommit={(revenueCollectionChannel) =>
            saveAttribute({
              revenueCollectionChannel: revenueCollectionChannel || null
            })
          }
          inputType="select"
          inputProps={{
            options: [
              "",
              "linked_bank",
              "ach_to_mercury",
              "billcom_manual_check"
            ].map((value) => ({
              value,
              label: value ? startCase(value) : "-"
            }))
          }}
        />
        {hasPms(partner, "buildium") && (
          <div className="border-2 p-4">
            <h3 className="font-bold border-top">Buildium Credentials</h3>
            <InlineEditable
              label="Buildium Email"
              value={partner.settings.buildiumEmail}
              displayValue={partner.settings.buildiumEmail}
              ListItemComponent={ListItem}
              onCommit={(buildiumEmail) =>
                saveAttribute({
                  settings: { ...partner.settings, buildiumEmail }
                })
              }
            />
            <InlineEditable
              label="Buildium Password"
              value={partner.settings.buildiumPassword}
              displayValue={partner.settings.buildiumPassword}
              ListItemComponent={ListItem}
              onCommit={(buildiumPassword) =>
                saveAttribute({
                  settings: { ...partner.settings, buildiumPassword }
                })
              }
            />
            <InlineEditable
              label="Buildium Subdomain"
              value={partner.settings.buildiumSubdomain}
              displayValue={partner.settings.buildiumSubdomain}
              ListItemComponent={ListItem}
              onCommit={(buildiumSubdomain) =>
                saveAttribute({
                  settings: { ...partner.settings, buildiumSubdomain }
                })
              }
            />
          </div>
        )}
        <InlineEditable
          label="Plaid bank linking redirect URL"
          value={partner.plaidBankLinkingRedirectUrl}
          ListItemComponent={ListItem}
          displayValue={
            <SafeExternalLink href={partner.plaidBankLinkingRedirectUrl}>
              {partner.plaidBankLinkingRedirectUrl}
            </SafeExternalLink>
          }
          onCommit={(plaidBankLinkingRedirectUrl) =>
            saveAttribute({ plaidBankLinkingRedirectUrl })
          }
        />

        </div>
        <div id="criminal-pricing">
        <InlineEditable
          label="Boom Report Identity Charge Price"
          value={(
            partner?.settings?.boomreportIdentityVerificationPriceCents / 100
          ).toFixed(2)}
          displayValue={
            <Money
              value={{
                cents:
                  partner?.settings?.boomreportIdentityVerificationPriceCents,
                currency: "USD"
              }}
            />
          }
          ListItemComponent={ListItem}
          onCommit={(boomreportIdentityVerificationPriceCents) =>
            saveAttribute({ boomreportIdentityVerificationPriceCents })
          }
        />

        <InlineEditable
          label="County: Criminal report flat price"
          inputType="money"
          value={(partner?.settings?.countyFlatPriceCents / 100).toFixed(2)}
          displayValue={
            <Money
              value={{
                cents: partner?.settings?.countyFlatPriceCents,
                currency: "USD"
              }}
            />
          }
          ListItemComponent={ListItem}
          onCommit={(countyFlatPriceCents) =>
            saveAttribute({ countyFlatPriceCents })
          }
        />

        <InlineEditable
          label="County: Walkin criminal report surcharge markup percentage"
          value={partner.walkinCriminalReportSurchargeMarkupPercentage}
          displayValue={partner.walkinCriminalReportSurchargeMarkupPercentage}
          ListItemComponent={ListItem}
          onCommit={(walkinCriminalReportSurchargeMarkupPercentage) =>
            saveAttribute({ walkinCriminalReportSurchargeMarkupPercentage })
          }
        />
        <InlineEditable
          label="County: Walkin criminal report surcharge markup amount"
          value={partner.walkinCriminalReportSurchargeMarkupAmount}
          displayValue={partner.walkinCriminalReportSurchargeMarkupAmount}
          ListItemComponent={ListItem}
          onCommit={(walkinCriminalReportSurchargeMarkupAmount) =>
            saveAttribute({ walkinCriminalReportSurchargeMarkupAmount })
          }
        />
        <InlineEditable
          label="County: Walkin criminal report base markup percentage"
          value={partner.walkinCriminalReportBaseMarkupPercentage}
          displayValue={partner.walkinCriminalReportBaseMarkupPercentage}
          ListItemComponent={ListItem}
          onCommit={(walkinCriminalReportBaseMarkupPercentage) =>
            saveAttribute({ walkinCriminalReportBaseMarkupPercentage })
          }
        />
        <InlineEditable
          label="County: Walkin criminal report base markup amount"
          value={partner.walkinCriminalReportBaseMarkupAmount}
          displayValue={partner.walkinCriminalReportBaseMarkupAmount}
          ListItemComponent={ListItem}
          onCommit={(walkinCriminalReportBaseMarkupAmount) =>
            saveAttribute({ walkinCriminalReportBaseMarkupAmount })
          }
        />

        <InlineEditable
          label="Statewide: Criminal report flat price"
          inputType="money"
          value={(partner?.settings?.stateFlatPriceCents / 100).toFixed(2)}
          displayValue={
            <Money
              value={{
                cents: partner?.settings?.stateFlatPriceCents,
                currency: "USD"
              }}
            />
          }
          ListItemComponent={ListItem}
          onCommit={(stateFlatPriceCents) =>
            saveAttribute({ stateFlatPriceCents })
          }
        />

        <InlineEditable
          label="Statewide: Walkin criminal report surcharge markup percentage"
          value={partner.statewideWalkinCriminalReportSurchargeMarkupPercentage}
          displayValue={
            partner.statewideWalkinCriminalReportSurchargeMarkupPercentage
          }
          ListItemComponent={ListItem}
          onCommit={(statewideWalkinCriminalReportSurchargeMarkupPercentage) =>
            saveAttribute({
              statewideWalkinCriminalReportSurchargeMarkupPercentage
            })
          }
        />

        <InlineEditable
          label="Statewide: Walkin criminal report surcharge markup amount"
          value={partner.statewideWalkinCriminalReportSurchargeMarkupAmount}
          displayValue={
            partner.statewideWalkinCriminalReportSurchargeMarkupAmount
          }
          ListItemComponent={ListItem}
          onCommit={(statewideWalkinCriminalReportSurchargeMarkupAmount) =>
            saveAttribute({
              statewideWalkinCriminalReportSurchargeMarkupAmount
            })
          }
        />

        <InlineEditable
          label="Statewide: Walkin criminal report base markup percentage"
          value={partner.statewideWalkinCriminalReportBaseMarkupPercentage}
          displayValue={
            partner.statewideWalkinCriminalReportBaseMarkupPercentage
          }
          ListItemComponent={ListItem}
          onCommit={(statewideWalkinCriminalReportBaseMarkupPercentage) =>
            saveAttribute({ statewideWalkinCriminalReportBaseMarkupPercentage })
          }
        />

        <InlineEditable
          label="Statewide: Walkin criminal report base markup amount"
          value={partner.statewideWalkinCriminalReportBaseMarkupAmount}
          displayValue={partner.statewideWalkinCriminalReportBaseMarkupAmount}
          ListItemComponent={ListItem}
          onCommit={(statewideWalkinCriminalReportBaseMarkupAmount) =>
            saveAttribute({ statewideWalkinCriminalReportBaseMarkupAmount })
          }
        />

        </div>

        {partner.features?.includes(
          "multiple_property_management_systems"
        ) && (
          <DetailSection
            title="Property Management System Integrations"
            id="pms-integrations"
          >
            {partner.integrationCredentials?.length > 0 ? (
              <SimpleTable
                truncated
                tableProps={{ size: "small" }}
                headers={[
                  "Name",
                  "Integration",
                  "Created At",
                  "Updated At"
                ]}
                rows={partner.integrationCredentials}
                keyRowAttr="id"
                toCells={(row) => [
                  row.name || "-",
                  startCase(row.integration) || "-",
                  row.createdAt ? dayjs(row.createdAt).format("MMM DD, YYYY") : "-",
                  row.updatedAt ? dayjs(row.updatedAt).format("MMM DD, YYYY") : "-",
                ]}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No Property Management System integrations set up yet.
              </Typography>
            )}
          </DetailSection>
        )}

        <Box id="partner-events" mt={5}>
          <SimpleTable
            truncated
            tableProps={{ size: "small" }}
            headers={[
              "Partner Events",
              "Service",
              "Name",
              "Created",
              "Processed",
              "Message"
            ]}
            rows={partner.events}
            toCells={(row) => [
              row.id,
              row.service,
              row.name,
              <Time key={1} format="MMM DD, YYYY HH:mm:ss">
                {row.createdAt}
              </Time>,
              <Time key={2} format="MMM DD, YYYY HH:mm:ss">
                {row.processedAt}
              </Time>,
              row.message
            ]}
          />
        </Box>
        <DetailSection title="Tiers List" id="pricing-tiers">
          <SimpleTable
            truncated
            tableProps={{ size: "small" }}
            headers={[
              "",
              "Tier",
              "Trial Ended On",
              "Lower Bound",
              "Upper Bound",
              "Enrollment fee",
              "Price per enrollment(Ongoing)",
              "Price per enrollment(Past Reporting)"
            ]}
            rows={tiers}
            keyRowAttr="id"
            toCells={(row) => [
              <PricingTierAction
                key="pt"
                onClick={(pt) => {
                  pricingTierToggle.turnOn();
                  setSelectedPricingTier(pt);
                }}
                pricingTier={row}
              />,
              row.name,
              row.trialEndedAt,
              row.lowerBound,
              row.upperBound,
              <Money key={`pre-${row.id}`} value={row.enrollmentFee} />,
              <Money
                key={`prog-${row.id}`}
                value={row.pricePerEnrollmentOngoing}
              />,
              <Money
                key={`prrp-${row.id}`}
                value={row.pricePerEnrollmentPastReporting}
              />
            ]}
          />
        </DetailSection>

        <DetailSection title="Exclusion List" id="exclusion-list">
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.open(`/partners/${id}/exclusions`, "_blank");
            }}
          >
            Open exclusion list
          </Button>
        </DetailSection>

        <DetailSection title="Workflow Analytics">
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.open(`/workflow-analytics?partner_id=${id}`, "_blank");
            }}
          >
            Open workflow analytics
          </Button>
        </DetailSection>

        <div id="monthly-billing">
        <InlineEditable
          label="Monthly Minimum Payment"
          displayValue={<Money value={partner.paymentMonthlyMinimum} />}
          value={(partner.paymentMonthlyMinimum?.cents / 100).toFixed(2)}
          onCommit={(dollars) =>
            saveAttribute({
              paymentMonthlyMinimum: {
                ...partner.paymentMonthlyMinimum,
                cents: dollars * 100
              }
            })
          }
        />

        <InlineEditable
          label="Payout per Enrollment Past Reporting"
          displayValue={
            <Money value={partner.payoutPerEnrollmentPastReporting} />
          }
          value={(
            partner.payoutPerEnrollmentPastReporting?.cents / 100
          ).toFixed(2)}
          onCommit={(dollars) =>
            saveAttribute({
              payoutPerEnrollmentPastReporting: {
                ...partner.payoutPerEnrollmentPastReporting,
                cents: dollars * 100
              }
            })
          }
        />

        </div>
        <div id="credit-bureau-ids">
        <InlineEditable
          label="Identification number (equifax)"
          value={partner.identificationNumbers?.equifax}
          ListItemComponent={ListItem}
          onCommit={(value) =>
            saveAttribute({
              identificationNumbers: {
                ...partner.identificationNumbers,
                equifax: value?.trim() || null
              }
            })
          }
        />

        <InlineEditable
          label="Identification number (experian)"
          value={partner.identificationNumbers?.experian}
          ListItemComponent={ListItem}
          onCommit={(value) =>
            saveAttribute({
              identificationNumbers: {
                ...partner.identificationNumbers,
                experian: value?.trim() || null
              }
            })
          }
        />

        <InlineEditable
          label="Identification number (transunion)"
          value={partner.identificationNumbers?.transunion}
          ListItemComponent={ListItem}
          onCommit={(value) =>
            saveAttribute({
              identificationNumbers: {
                ...partner.identificationNumbers,
                transunion: value?.trim() || null
              }
            })
          }
        />

        </div>

        <BillingSlice partner={partner} />

        <div id="features">
        <Features
          partner={partner}
          onSave={(features) => {
            saveAttribute({ features });
          }}
        />
        </div>

        <DetailSection title="BoomScreen email settings" id="boomscreen-email">
          <InlineEditable
            label="Conditional Approval Email Message"
            value={partner.settings?.conditionalApprovalEmailMessage || ""}
            displayValue={
              partner.settings?.conditionalApprovalEmailMessage ||
              "(Not set - uses default)"
            }
            onCommit={(conditionalApprovalEmailMessage) =>
              saveAttribute({
                settings: {
                  ...partner.settings,
                  conditional_approval_email_message:
                    conditionalApprovalEmailMessage || null
                }
              })
            }
            inputProps={{ multiline: true, rows: 4 }}
          />
          <Box mt={2}>
            <InlineEditable
              label="Approval Email Message"
              value={partner.settings?.approvalEmailMessage || ""}
              displayValue={
                partner.settings?.approvalEmailMessage ||
                "(Not set - uses default)"
              }
              onCommit={(approvalEmailMessage) =>
                saveAttribute({
                  settings: {
                    ...partner.settings,
                    approval_email_message: approvalEmailMessage || null
                  }
                })
              }
              inputProps={{ multiline: true, rows: 4 }}
            />
          </Box>
          <Box mt={2}>
            <InlineEditable
              label="Declined Email Message"
              value={partner.settings?.declinedEmailMessage || ""}
              displayValue={
                partner.settings?.declinedEmailMessage ||
                "(Not set - uses default)"
              }
              onCommit={(declinedEmailMessage) =>
                saveAttribute({
                  settings: {
                    ...partner.settings,
                    declined_email_message: declinedEmailMessage || null
                  }
                })
              }
              inputProps={{ multiline: true, rows: 4 }}
            />
          </Box>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              Available placeholders: [First name], [Partner name], [Landlord
              name], [property address], [unit], [unit, property address]. You
              can combine any placeholders with commas. Leave empty to use
              default message.
            </Typography>
          </Box>
        </DetailSection>

        <div id="crm-billing">
        <CrmBillingSlice partner={partner} saveAttribute={saveAttribute} />
        </div>

        <PhoneNumberBillingSlice partner={partner} saveAttribute={saveAttribute} />

        <PhoneNumbersSlice partnerId={partnerId || id} />

        <DetailSection title="Rent Reporting" id="rent-reporting">
          <FormControlLabel
            control={
              <Switch
                checked={partner.pmsUseTenantAddressForEnrollment === true}
                onChange={(e) => {
                  saveAttribute({
                    settings: {
                      ...partner.settings,
                      pms_use_tenant_address_for_enrollment: e.target.checked
                    }
                  });
                }}
              />
            }
            label="Enable using Tenant Address for Enrollment"
          />

          <Box mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoEnrollmentEnabled}
                  onChange={(e) => setAutoEnrollmentEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Auto Enrollment"
            />
            {partner.autoEnrollmentEnabledDate && (
              <Typography variant="caption" color="textSecondary" display="block">
                Enabled on:{" "}
                <Time format="MMM DD, YYYY">
                  {partner.autoEnrollmentEnabledDate}
                </Time>
              </Typography>
            )}
            {autoEnrollmentEnabled && (
              <Box mt={1} mb={1}>
                <FormControl fullWidth>
                  <TextField
                    label="Auto Enrollment Default"
                    value={autoEnrollmentDefault}
                    onChange={(e) => setAutoEnrollmentDefault(e.target.value)}
                    select
                    fullWidth
                  >
                    <MenuItem value="opt_in">Opt-in</MenuItem>
                    <MenuItem value="opt_out">Opt-out</MenuItem>
                    <MenuItem value="direct">Direct</MenuItem>
                    {partner.defaultEnrollmentType && (
                      <MenuItem value="default">Partner Default</MenuItem>
                    )}
                  </TextField>
                </FormControl>
              </Box>
            )}
            {!autoEnrollmentEnabled && partner.autoEnrollmentDefault && (
              <Typography variant="body2" display="block">
                Current default:{" "}
                <strong>{partner.autoEnrollmentDefault}</strong>
              </Typography>
            )}
            <Box mt={1}>
              <Button
                variant="contained"
                color="primary"
                disabled={(() => {
                  const currentEnabled = Boolean(
                    partner.autoEnrollmentEnabledDate
                  );
                  const currentDefault = partner.autoEnrollmentDefault || "";
                  const hasChanges =
                    autoEnrollmentEnabled !== currentEnabled ||
                    autoEnrollmentDefault !== currentDefault;
                  const isValid =
                    !autoEnrollmentEnabled ||
                    (autoEnrollmentEnabled && autoEnrollmentDefault.trim());
                  return !hasChanges || !isValid;
                })()}
                onClick={() => {
                  saveAttribute({
                    auto_enrollment_enabled: autoEnrollmentEnabled ? true : false,
                    auto_enrollment_default: autoEnrollmentDefault?.trim() || null
                  });
                }}
              >
                Save
              </Button>
            </Box>
          </Box>

          <Box mt={2}>
            <InlineEditable
              label="Opt out period in days"
              value={partner.optOutPeriodDays}
              displayValue={partner.optOutPeriodDays}
              ListItemComponent={CompactListItem}
              onCommit={(optOutPeriodDays) => saveAttribute({ optOutPeriodDays })}
            />
            <InlineEditable
              label="Opt in period in days"
              value={partner.optInPeriodDays}
              displayValue={partner.optInPeriodDays}
              ListItemComponent={CompactListItem}
              onCommit={(optInPeriodDays) => saveAttribute({ optInPeriodDays })}
            />
            <InlineEditable
              type="number"
              label="Max days to unenroll a customer with pending verifications"
              value={partner.maxDaysToUnenrollPendingVerificationEnrollments}
              displayValue={partner.maxDaysToUnenrollPendingVerificationEnrollments}
              ListItemComponent={CompactListItem}
              onCommit={(maxDaysToUnenrollPendingVerificationEnrollments) =>
                saveAttribute({ maxDaysToUnenrollPendingVerificationEnrollments })
              }
            />
            <InlineEditable
              label="Verification balance threshold in $"
              value={partner.verificationBalanceThreshold}
              displayValue={partner.verificationBalanceThreshold}
              ListItemComponent={CompactListItem}
              onCommit={(verificationBalanceThreshold) =>
                saveAttribute({ verificationBalanceThreshold })
              }
            />
          </Box>

          <Box mt={2}>
            <Typography variant="body2">
              Default enrollment type: {partner.defaultEnrollmentType || "Not set"}
            </Typography>

            {partner.stateProvinceDefaults &&
            partner.stateProvinceDefaults.length > 0 ? (
              <Box mt={1} mb={2}>
                <SimpleTable
                  truncated
                  tableProps={{ size: "small", style: { tableLayout: "fixed" } }}
                  headers={["State/province overrides", "Enrollment type"]}
                  rows={partner.stateProvinceDefaults}
                  toCells={(row) => [row.stateProvince, row.defaultEnrollmentType]}
                />
              </Box>
            ) : (
              <Typography variant="caption" color="textSecondary" display="block">
                No state/provinces overrides configured
              </Typography>
            )}

            {partner.propertyDefaults &&
            partner.propertyDefaults.length > 0 ? (
              <Box mt={1} mb={2}>
                <SimpleTable
                  truncated
                  tableProps={{ size: "small", style: { tableLayout: "fixed" } }}
                  headers={["Property overrides", "Enrollment type"]}
                  rows={partner.propertyDefaults}
                  toCells={(row) => [row.propertyName, row.defaultEnrollmentType]}
                />
              </Box>
            ) : (
              <Typography variant="caption" color="textSecondary" display="block">
                No property overrides configured
              </Typography>
            )}
          </Box>

          <Box mt={2}>
            <Typography variant="body2">
              Enrollment eligibility date:{" "}
              {partner.enrollmentEligibilityDate
                ? dayjs(partner.enrollmentEligibilityDate).format("DD MMM YYYY")
                : "Not set"}
            </Typography>
            {!partner.enrollmentEligibilityDate && (
              <Typography variant="caption" color="textSecondary" display="block">
                No eligibility date has been configured
              </Typography>
            )}
          </Box>
        </DetailSection>

        <DetailSection title="Credit Monitoring Enrollments" id="credit-monitoring">
          <InlineEditable
            label="Max simultaneous customers enrolled on credit monitoring"
            value={partner.maxCreditMonitoringCustomers}
            ListItemComponent={ListItem}
            inputProps={{
              type: "number"
            }}
            onCommit={(value) =>
              saveAttribute({
                settings: {
                  ...partner.settings,
                  max_credit_monitoring_customers: value
                }
              })
            }
          />
          <FormControl margin="normal" fullWidth>
            <DatePicker
              label="Credit Impact Dashboard Start Date"
              format="YYYY-MM-DD"
              value={partner.creditImpactDashboardCutoffDate || null}
              disabled={
                !partner?.features?.includes("enable_credit_impact_dashboard")
              }
              onChange={(value) =>
                saveAttribute({
                  settings: {
                    ...partner.settings,
                    credit_impact_dashboard_cutoff_date:
                      value?.format("YYYY-MM-DD")
                  }
                })
              }
            />
          </FormControl>
        </DetailSection>

        <DetailSection title="Whitelist" id="whitelist">
          <FormControlLabel
            control={
              <Switch checked={partner.allowedCustomerEvents !== null} />
            }
            label={`${
              partner.allowedCustomerEvents === null ? "Enable" : "Disable"
            } event filtering`}
            onClick={(e) => {
              saveAttribute({
                allowed_customer_events:
                  partner.allowedCustomerEvents === null ? [] : null
              });
            }}
          />

          {partner.allowedCustomerEvents !== null ? (
            <>
              <Typography variant="body2">
                <span style={{ fontWeight: "bold" }}>Note:</span> Empty white
                list is not the same as &quot;Disable event filtering&quot;.
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
                {`- Empty white list means no events will be sent to partner's customers.
- Upon clicking on "Disable event filtering" - all events will be sent to partner's customers.
- Non-empty white list - only events in the white list will be sent to partner's customers.
- Overridden means that partner's customer will get a "special version of the event"`}
              </Typography>
              <TransferList
                data={partner.allowedCustomerEvents}
                onSave={(data) => {
                  saveAttribute({
                    allowed_customer_events: data
                  });
                }}
              />
            </>
          ) : null}
        </DetailSection>

        <div id="external-links">
        <ExternalLinksList links={partner.externalLinks} />
        </div>

        <DetailSection title="Ledger Configuration" id="ledger-config">
          <LedgerConfigurationModal
            partnerId={partnerId || id}
            partner={partner}
            saveAttribute={saveAttribute}
          />
        </DetailSection>


        <div id="admin-actions">
        <AdminActionLinks
          links={[
            ...(partner.adminActionLinks || []),
            ...(hasAnyPms(partner, supportedPropertyManagementSystemForPostingCharges)
              ? [
                  {
                    name: "Post Rent Reporting Charges to PMS",
                    url: `/admin/v1/partners/${partner.id}/post_rent_reporting_charges`,
                    httpMethod: "post",
                    params: {},
                    help: "Post rent reporting charges to resident ledgers in PMS"
                  }
                ]
              : []),
            ...(!partner.plaidClientId
              ? [
                  {
                    name: "Enable Plaid for BoomScreen",
                    url: `/admin/v1/partners/${partner.id}/create_plaid_customer`,
                    httpMethod: "post",
                    params: {
                      company_name: partner.name,
                      legal_entity_name:
                        partner.legalEntity?.name || partner.name,
                      application_name: `${partner.name} App`,
                      website: partner.url,
                      is_diligence_attested: true,
                      address: {
                        street: partner.legalEntity?.address?.address1 || "",
                        city: partner.legalEntity?.address?.city || "",
                        region:
                          partner.legalEntity?.address?.stateOrProvince || "",
                        postal_code:
                          partner.legalEntity?.address?.postalCode || "",
                        country_code:
                          partner.legalEntity?.address?.country || ""
                      }
                    },
                    help: "Create Plaid customer for BoomScreen integration"
                  }
                ]
              : [])
          ]}
          partner={partner}
          onActionSuccess={handleActionSuccess}
          onActionError={handleActionError}
        />
        {partner.plaidClientId && partner.pendingPlaidEnablement && config.appEnv !== "production" && (
          <Box mt={1} mb={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  await api.post(
                    `/admin/v1/partners/${partner.id}/enable_plaid_customer`,
                    {}
                  );
                  setPartner((prev) => ({ ...prev, pendingPlaidEnablement: false }));
                  toast.success("Plaid for BoomScreen enabled successfully");
                } catch (enableError) {
                  const plaidError = enableError?.response?.data?.error?.message;
                  toast.error(
                    `Error enabling Plaid: ${
                      plaidError?.errorMessage ||
                      plaidError?.error ||
                      "Failed to enable Plaid for BoomScreen"
                    }`
                  );
                }
              }}
            >
              DO NOT CLICK (status needs to be pending)
            </Button>
          </Box>
        )}
        {partner.plaidClientId && (
          <Box mt={1} mb={1} display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              variant="contained"
              style={
                partner.settings?.plaidIncomeSubcodesEnabled
                  ? { backgroundColor: "#d32f2f", color: "#fff" }
                  : { backgroundColor: "#388e3c", color: "#fff" }
              }
              onClick={() =>
                saveAttribute({
                  settings: {
                    ...partner.settings,
                    plaidIncomeSubcodesEnabled: !partner.settings?.plaidIncomeSubcodesEnabled
                  }
                })
              }
            >
              {partner.settings?.plaidIncomeSubcodesEnabled
                ? "Disable Plaid Income Subcodes"
                : "Enable Plaid Income Subcodes"}
            </Button>
            <Typography>
              {partner.settings?.plaidIncomeSubcodesEnabled
                ? "Partner's Plaid subcodes are being used for income verification. DISABLE to  use global creds instead"
                : "Partner already has Plaid subcodes. Enable to use them for income verification"}
            </Typography>
          </Box>
        )}
        {hasPms(partner, "yardi") && (
          <PostYardiCharges partnerId={partnerId || id} />
        )}

        {hasAnyPms(partner, supportedPropertyManagementSystemForPostingCharges) || hasPms(partner, "yardi") ? (
          <div className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="secondary"
              component={RouterLink}
              to={`/partners/${id}/pms-charges`}
            >
              PMS Charges
            </Button>

            <Typography>Open PMS charges list page</Typography>
          </div>
        ) : null}

        <div className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="secondary"
            component={RouterLink}
            to={`/partners/${id}/webhook-requests`}
          >
            Webhook Requests
          </Button>

          <Typography>View webhook request history</Typography>
        </div>

        <div className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="secondary"
            component={RouterLink}
            to={`/partners/${id}/lead-imports`}
          >
            Import Leads
          </Button>

          <Typography>Upload a CSV of leads and review the column mapping before import</Typography>
        </div>

        <div className={classes.buttonGroup}>
          <TransUnionCredsModal partner={partner} saveAttribute={saveAttribute} />

          <Typography>Manage TransUnion credentials for this partner</Typography>
        </div>

        <DetailSection title="Customer Identity Management">
          <FillCustomerIdentityViaCsv partnerId={partnerId || id} />
        </DetailSection>
        </div>
      </AlignedListContainer>
      <Box id="payment-instruments" mt={5}>
        <SimpleTable
          truncated
          tableProps={{ size: "small" }}
          headers={["Instrument", "Name", "Institution", "Last4"]}
          rows={paymentInstruments}
          keyRowAttr="id"
          toCells={(row) => [
            <Link key={1} to={row.adminLink}>
              {startCase(row.paymentMethodType)} ({row.id})
            </Link>,
            <Link key={3} to={row.adminLink}>
              {row.name}
            </Link>,
            row.display.institutionName,
            row.display.last4
          ]}
        />
      </Box>
      <DetailSection title="Money Tools" id="money-tools">
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            href={`/partners/${partner.id}/invoices`}
          >
            Invoices
          </Button>
          <Button
            variant="contained"
            color="primary"
            href={`/partners/${partner.id}/payouts`}
          >
            Payouts
          </Button>
          <Button
            variant="contained"
            color="primary"
            href={`/statements?partnerId=${partner.id}`}
          >
            Statements
          </Button>
          <Button
            variant="contained"
            color="primary"
            href={`/aggregated-payments/new?partnerId=${partner.id}&partnerName=${encodeURIComponent(partner.name)}`}
          >
            Aggregated Payments
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOneOffChargeModal(true)}
            disabled={!(partnerId || id)}
          >
            Add One-Off Charge / Refund
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setRecurringChargesDialogOpen(true)}
            disabled={!(partnerId || id)}
          >
            Recurring Charges
            {recurringCharges.length > 0 && ` (${recurringCharges.length})`}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setUpcomingDialogOpen(true)}
          >
            Upcoming Activities
            {upcomingStatements.upcomingActivities?.length
              ? ` (${upcomingStatements.upcomingActivities.length} Items, Total: `
              : ""}
            {upcomingStatements.balance && (
              <Money value={upcomingStatements.balance} />
            )}
            {upcomingStatements.upcomingActivities?.length ? ")" : ""}
          </Button>
        </Box>
      </DetailSection>

      <Dialog
        open={upcomingDialogOpen}
        onClose={() => setUpcomingDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Upcoming Activities — {UpcomingTitle}:{" "}
          <Money value={upcomingStatements.balance} />
          {upcomingStatements.billingDate && (
            <Typography variant="body2" component="span" style={{ marginLeft: 16 }}>
              On: <Time formatExact="DD MMM YYYY">{upcomingStatements.billingDate}</Time>
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <SimpleTable
            truncated
            tableProps={{ size: "small" }}
            headers={[
              "Product",
              "Item",
              "Name",
              "Email",
              "Property Name",
              "Property Address",
              "Unit",
              "Amount"
            ]}
            rows={upcomingStatements.upcomingActivities}
            keyRowAttr="id"
            toCells={(row) => [
              <ListItem key={1}> {row.metadata.product} </ListItem>,
              <ListItem key={2}>{activityDescription(row)}</ListItem>,
              <ListItem key={3}> {row.metadata.name} </ListItem>,
              <ListItem key={4}>
                <div
                  style={{
                    maxWidth: 100,
                    wordBreak: "break-word"
                  }}
                >
                  {row.metadata.email}
                </div>
              </ListItem>,
              <ListItem key={5}> {row.metadata.propertyName} </ListItem>,
              <ListItem key={6}> {row.metadata.oneLineAddress} </ListItem>,
              <ListItem key={7}> {row.metadata.unit} </ListItem>,
              <Money key={8} value={row.amount} />
            ]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpcomingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <DetailSection title="Audit Logs" id="audit-logs">
        <Suspense fallback={<div>Loading...</div>}>
          <AuditLogs auditableId={partner.id} auditableType="Boom::Partner" />
        </Suspense>
      </DetailSection>
      <DetailSection title="Pets" id="pets">
        <InlineEditable
          label="Pets Screening URL"
          value={partner.settings?.petScreeningUrl}
          ListItemComponent={ListItem}
          onCommit={(value) =>
            saveAttribute({
              settings: {
                ...partner.settings,
                petScreeningUrl: value
              }
            })
          }
        />
      </DetailSection>
      <DetailSection title="Verification Schema Prices" id="verification-prices">
        <SimpleTable
          tableProps={{ size: "small" }}
          headers={["Verification Schema", "Price"]}
          rows={verificationNames}
          toCells={(row) => [
            startCase(row),
            row === "credit_report" ? (
              <CreditModelPriceInput
                saveAttribute={saveAttribute}
                partner={partner}
                ficoPrice={(getCreditModelPrice("credit_report_05206") / 100).toFixed(2)}
                vantagePrice={(getCreditModelPrice("credit_report_05402") / 100).toFixed(
                  2
                )}
                residentScorePrice={(getCreditModelPrice("credit_report_00A72") / 100).toFixed(2)}
              />
            ) : (
              <InlineEditable
                key={row}
                displayValue={
                  <Money
                    value={{
                      cents: verificationSchemaPrice(row)
                    }}
                  />
                }
                value={(verificationSchemaPrice(row) / 100).toFixed(2)}
                onCommit={(dollars) => {
                  const updatedFees = {
                    ...partner.boomScreenScreeningFees,
                    [humps.camelize(row)]: dollars * 100,
                  };

                  saveAttribute({
                    settings: {
                      ...partner.settings,
                      boomScreenScreeningFees: updatedFees
                    }
                  });
                }}
              />
            )
          ]}
        />
      </DetailSection>

      <PricingTierForm
        toggle={pricingTierToggle}
        pricingTier={selectedPricingTier}
        error={pricingTierError}
        onCommit={(params, doCreate) => {
          const data = {
            ...params,
            partner_id: partner.id,
            tier_id: selectedPricingTier.id
          };
          if (doCreate)
            api
              .createPricingTier(data)
              .then(api.pickData)
              .then(setPartner)
              .then(pricingTierToggle.turnOff)
              .catch(setPricingTierError);
          else
            api
              .updatePricingTier(data)
              .then(api.pickData)
              .then(setPartner)
              .then(pricingTierToggle.turnOff)
              .catch(setPricingTierError);
        }}
      />

      <ConfirmationDialog
        title="Edit"
        open={Boolean(partnerUserModal)}
        onCancel={() => {
          setPartnerUserModal(false);
          setNewPassword("");
        }}
        onConfirm={() => {
          const payload = {};

          if (newPassword) payload.password = newPassword;

          api
            .patchPartnerUser(partnerUserModal.id, payload)
            .then((response) => {
              setPartnerUserModal(false);
              toast.success(`User ${response.data.name} updated successfully!`);
            })
            .catch((error) => {
              toast.error(error.message);
            });
        }}
        text="Are you sure?"
        cancelProps={{ children: "Go back" }}
        confirmProps={{
          children: "Update",
          disabled: !newPassword
        }}
      >
        <Box>
          <FormControl
            margin={partnerUserModal.role !== "owner" ? "normal" : "none"}
            fullWidth
          >
            <TextField
              required
              label="Password"
              type="password"
              variant="outlined"
              onChange={(e) => {
                setNewPassword(e.target.value);
              }}
            />
          </FormControl>
        </Box>
      </ConfirmationDialog>

      <Dialog
        open={recurringChargesDialogOpen}
        onClose={() => setRecurringChargesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Monthly Recurring Charges
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingRecurringChargeId(null);
                setRecurringChargeData({ amount: "", description: "" });
                setRecurringChargeFormOpen(true);
              }}
            >
              Add
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {recurringCharges.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No recurring charges configured.
            </Typography>
          ) : (
            <SimpleTable
              headers={["Description", "Amount", "Created", "Actions"]}
              rows={recurringCharges}
              keyRowAttr="id"
              toCells={(row) => [
                row.description,
                <Money key="amount" value={row.amount} />,
                <Time key="time" formatExact="DD MMM YYYY">{row.createdAt}</Time>,
                <Box key="actions" display="flex" style={{ gap: 4 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingRecurringChargeId(row.id);
                      setRecurringChargeData({
                        amount: row.amount ? Math.abs(row.amount.cents) / 100 : "",
                        description: row.description
                      });
                      setRecurringChargeFormOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (!window.confirm(`Remove recurring charge "${row.description}"?`)) return;
                      api.deletePartnerRecurringCharge({ id: partner.id, chargeId: row.id })
                        .then(() => {
                          toast.success("Recurring charge removed");
                          fetchRecurringCharges();
                        })
                        .catch((error) => toast.error(`Error: ${error.message}`));
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringChargesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title={editingRecurringChargeId ? "Edit Recurring Charge" : "Add Recurring Charge"}
        open={recurringChargeFormOpen}
        onCancel={() => {
          setRecurringChargeFormOpen(false);
          setRecurringChargeData({ amount: "", description: "" });
          setEditingRecurringChargeId(null);
        }}
        onConfirm={() => {
          setRecurringChargeSubmitting(true);
          const amountInCents = Math.round(parseFloat(recurringChargeData.amount) * 100);
          const payload = {
            id: partner.id,
            amount: { cents: amountInCents, currency: "USD" },
            description: recurringChargeData.description
          };

          const apiCall = editingRecurringChargeId
            ? api.updatePartnerRecurringCharge({ ...payload, chargeId: editingRecurringChargeId })
            : api.createPartnerRecurringCharge(payload);

          apiCall
            .then(() => {
              setRecurringChargeFormOpen(false);
              setRecurringChargeData({ amount: "", description: "" });
              setEditingRecurringChargeId(null);
              fetchRecurringCharges();
              toast.success(
                editingRecurringChargeId
                  ? "Recurring charge updated!"
                  : "Recurring charge added!"
              );
            })
            .catch((error) => toast.error(`Error: ${error.message}`))
            .finally(() => setRecurringChargeSubmitting(false));
        }}
        text="This charge will be automatically added to each billing statement."
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: editingRecurringChargeId ? "Update" : "Add",
          disabled: !recurringChargeData.amount || !recurringChargeData.description || recurringChargeSubmitting
        }}
      >
        <Box>
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={recurringChargeData.description}
            onChange={(e) => setRecurringChargeData({ ...recurringChargeData, description: e.target.value })}
            placeholder="e.g. Ledgering, Manual Invoicing, Reports"
          />
          <TextField
            label="Amount ($)"
            type="number"
            fullWidth
            margin="normal"
            value={recurringChargeData.amount}
            onChange={(e) => setRecurringChargeData({ ...recurringChargeData, amount: e.target.value })}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Box>
      </ConfirmationDialog>

      <ConfirmationDialog
        title={
          oneOffChargeData.operationType === "refund"
            ? "Add Refund"
            : "Add One-Off Charge"
        }
        open={oneOffChargeModal}
        onCancel={() => {
          setOneOffChargeModal(false);
          setOneOffChargeData({
            amount: "",
            description: "",
            category: "manual_one_off_charge",
            countTowardMonthlyMinimum: true,
            operationType: "charge"
          });
        }}
        onConfirm={() => {
          const amountInCents = Math.round(
            parseFloat(oneOffChargeData.amount) * 100
          );
          const finalAmount =
            oneOffChargeData.operationType === "refund"
              ? -amountInCents
              : amountInCents;

          api
            .addPartnerOneOffCharge({
              id: partner.id,
              amount: {
                cents: finalAmount,
                currency: "USD"
              },
              description: oneOffChargeData.description,
              category: oneOffChargeData.category,
              count_toward_monthly_minimum:
                oneOffChargeData.countTowardMonthlyMinimum
            })
            .then(() => {
              setOneOffChargeModal(false);
              setOneOffChargeData({
                amount: "",
                description: "",
                category: "manual_one_off_charge",
                countTowardMonthlyMinimum: true,
                operationType: "charge"
              });
              toast.success(
                oneOffChargeData.operationType === "refund"
                  ? "Refund added successfully!"
                  : "One-off charge added successfully!"
              );
            })
            .catch((error) => {
              toast.error(
                `Error adding ${oneOffChargeData.operationType}: ${error.message}`
              );
            });
        }}
        text={
          oneOffChargeData.operationType === "refund"
            ? "Add a refund to this partner's billing"
            : "Add a one-off charge to this partner's billing"
        }
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children:
            oneOffChargeData.operationType === "refund"
              ? "Add Refund"
              : "Add Charge",
          disabled: !oneOffChargeData.amount || !oneOffChargeData.description
        }}
      >
        <Box>
          <FormControl margin="normal" fullWidth>
            <FormLabel>Operation Type</FormLabel>
            <RadioGroup
              value={oneOffChargeData.operationType}
              onChange={(e) =>
                setOneOffChargeData({
                  ...oneOffChargeData,
                  operationType: e.target.value
                })
              }
            >
              <FormControlLabel
                value="charge"
                control={<Radio />}
                label="Charge (debit)"
              />
              <FormControlLabel
                value="refund"
                control={<Radio />}
                label="Refund (credit)"
              />
            </RadioGroup>
          </FormControl>

          <FormControl margin="normal" fullWidth>
            <TextField
              required
              label={`Amount (USD) - ${
                oneOffChargeData.operationType === "refund"
                  ? "Refund"
                  : "Charge"
              }`}
              type="number"
              step="0.01"
              variant="outlined"
              value={oneOffChargeData.amount}
              helperText={
                oneOffChargeData.operationType === "refund"
                  ? "Will be credited to partner account"
                  : "Will be charged to partner account"
              }
              onChange={(e) =>
                setOneOffChargeData({
                  ...oneOffChargeData,
                  amount: e.target.value
                })
              }
            />
          </FormControl>

          <FormControl margin="normal" fullWidth>
            <TextField
              required
              label="Description"
              type="text"
              variant="outlined"
              value={oneOffChargeData.description}
              onChange={(e) =>
                setOneOffChargeData({
                  ...oneOffChargeData,
                  description: e.target.value
                })
              }
            />
          </FormControl>

          <FormControl margin="normal" fullWidth>
            <TextField
              label="Category"
              type="text"
              variant="outlined"
              value={oneOffChargeData.category}
              onChange={(e) =>
                setOneOffChargeData({
                  ...oneOffChargeData,
                  category: e.target.value
                })
              }
            />
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={oneOffChargeData.countTowardMonthlyMinimum}
                onChange={(e) =>
                  setOneOffChargeData({
                    ...oneOffChargeData,
                    countTowardMonthlyMinimum: e.target.checked
                  })
                }
              />
            }
            label="Count toward monthly minimum"
          />
        </Box>
      </ConfirmationDialog>
      </div>
      </div>
    </AdminContainer>
  );
}

function PricingTierAction({ pricingTier, onClick }) {
  if (pricingTier?.id)
    return (
      <IconButton
        onClick={() => {
          onClick(pricingTier);
        }}
        key={1}
      >
        <EditIcon color="primary" />
      </IconButton>
    );

  return (
    <IconButton
      onClick={() => {
        onClick({});
      }}
      key={1}
    >
      <AddIcon color="primary" />
    </IconButton>
  );
}

function activityDescription(activity) {
  if (
    Object.keys(CREDITSCORE_MODELS).includes(
      activity.metadata?.creditScoreModelNumber
    )
  )
    return (
      activity.description +
      ` - ${CREDITSCORE_MODELS[activity.metadata?.creditScoreModelNumber]}`
    );

  return activity.description;
}

function ImpersonateUserAction({ user, onClick, disabled, partnerName }) {
  const [showConfirm, setShowConfirm] = React.useState(false);

  if (!user?.id) return null;

  if (disabled) {
    return (
      <Typography variant="caption" color="textSecondary">
        Disabled
      </Typography>
    );
  }

  const isBoompayUser = user.email?.endsWith("@boompay.app");

  return (
    <>
      <IconButton onClick={() => setShowConfirm(true)} key={user.id}>
        <AirlineStopsIcon color={isBoompayUser ? "primary" : "warning"} />
      </IconButton>
      <ConfirmationDialog
        open={showConfirm}
        title="Confirm Impersonation"
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          setShowConfirm(false);
          onClick(user);
        }}
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: "Proceed",
          color: isBoompayUser ? "primary" : "secondary"
        }}
      >
        <Box>
          {!isBoompayUser && (
            <Typography
              variant="body1"
              style={{ color: "#d32f2f", fontWeight: "bold", marginBottom: 16 }}
            >
              WARNING: You are about to impersonate a NON-BoomPay user (
              {user.email}). Only proceed if absolutely necessary for support or
              debugging purposes.
            </Typography>
          )}
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Partner: <strong>{partnerName}</strong>
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            User: <strong>{user.email}</strong>
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#666", fontStyle: "italic" }}
          >
            This action will be logged and attributed to your admin account.
          </Typography>
        </Box>
      </ConfirmationDialog>
    </>
  );
}

function PricingTierForm({ toggle, pricingTier, error, resetError, onCommit }) {
  const [fields, setFields] = React.useState({ ...pricingTier });

  React.useEffect(() => {
    setFields({ ...pricingTier });
  }, [pricingTier]);

  function mergeFields(p) {
    setFields({ ...fields, ...p });
  }

  function handleConfirm() {
    const doCreate = pricingTier?.id ? false : true;
    onCommit(fields, doCreate);
  }

  const valid =
    pricingTier?.id ||
    (fields.lowerBound &&
      fields.upperBound &&
      fields.name &&
      fields.enrollmentFee &&
      fields.pricePerEnrollmentOngoing);

  return (
    <ConfirmationDialog
      title="Edit Pricing Tier"
      open={toggle.on}
      onCancel={toggle.turnOff}
      onConfirm={handleConfirm}
      cancelProps={{ children: "Go back" }}
      confirmProps={{ children: "Save", disabled: !valid }}
      error={error}
      resetError={resetError}
    >
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Name"
          type="text"
          value={fields.name}
          variant="outlined"
          onChange={(e) => mergeFields({ name: e.target.value })}
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <DatePicker
          label="Trial Ended On"
          format="MM/DD/YYYY"
          value={fields.trialEndedAt ? fields.trialEndedAt : null}
          disablePast
          onChange={(value) =>
            mergeFields({ trialEndedAt: dayjs(value).format("YYYY-MM-DD") })
          }
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Lower Bound"
          type="number"
          value={fields.lowerBound}
          variant="outlined"
          onChange={(e) => mergeFields({ lowerBound: e.target.value })}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="Upper Bound"
          type="number"
          value={fields.upperBound}
          variant="outlined"
          onChange={(e) => mergeFields({ upperBound: e.target.value })}
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <TextField
          label={
            <>
              Enrollment Fee: <Money>{fields.enrollmentFee}</Money>
            </>
          }
          type="number"
          value={fields.enrollmentFee?.cents}
          variant="outlined"
          onChange={(e) =>
            mergeFields({
              enrollmentFee: merge({}, fields.enrollmentFee, {
                cents: e.target.value
              })
            })
          }
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <TextField
          label={
            <>
              Price per enrollment(Ongoing):{" "}
              <Money>{fields.pricePerEnrollmentOngoing}</Money>
            </>
          }
          type="number"
          value={fields.pricePerEnrollmentOngoing?.cents}
          variant="outlined"
          onChange={(e) =>
            mergeFields({
              pricePerEnrollmentOngoing: merge(
                {},
                fields.pricePerEnrollmentOngoing,
                {
                  cents: e.target.value
                }
              )
            })
          }
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <TextField
          label={
            <>
              Price per enrollment(Past Reporting):{" "}
              <Money>{fields.pricePerEnrollmentPastReporting}</Money>
            </>
          }
          type="number"
          value={fields.pricePerEnrollmentPastReporting?.cents}
          variant="outlined"
          onChange={(e) =>
            mergeFields({
              pricePerEnrollmentPastReporting: merge(
                {},
                fields.pricePerEnrollmentPastReporting,
                {
                  cents: e.target.value
                }
              )
            })
          }
        />
      </FormControl>
    </ConfirmationDialog>
  );
}
